import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { FaUser, FaEnvelope, FaPhone, FaCalendar, FaComment, FaAngleDown, FaMapMarkerAlt, FaEdit, FaRuler, FaBox, FaClock, FaLink, FaPrint } from 'react-icons/fa';
import { Heading } from '../../../components';

import axios from "axios";

// Backend API endpoint to submit form data
const API_PATH = 'https://acme-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

// Regular expressions for input validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const GetQuote = () => {
  // Initialize form fields
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    number: '',
    serviceType: '',
    projectTitle: '',
    materialType: '',
    quantity: '',
    dimensions: '',
    designLink: '',
    address: '',
    date: '',
    time: '',
    requirements: ''
  });

  // State for submission status and error messages
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Handle form submission
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      // Perform field validations
      if (!formData.fullName.trim()) validationErrors.push('Full name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.serviceType) validationErrors.push('Service type is required');
      if (!formData.projectTitle) validationErrors.push('Project title is required');
      if (!formData.materialType) validationErrors.push('Material type is required');
      if (!formData.quantity) validationErrors.push('Quantity is required');
      if (!formData.dimensions) validationErrors.push('Size/Dimensions are required');
      if (!formData.address) validationErrors.push('Address is required');
      if (!formData.date) validationErrors.push('Date is required');
      if (!formData.time) validationErrors.push('Time is required');

      // If errors exist, throw as one error message
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'designFile' && value) {
          postData.append(key, value, value.name);
        } else {
          postData.append(key, value);
        }
      });

      // Send form data to API
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle unsuccessful API response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      // On success, reset form and show success message
      setMailSent(true);
      setFormData({
        fullName: '',
        email: '',
        number: '',
        serviceType: '',
        projectTitle: '',
        materialType: '',
        quantity: '',
        dimensions: '',
        designFile: null,
        address: '',
        date: '',
        time: '',
        requirements: ''
      });
    } 
    // Show error message
    catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      // Stop loading spinner
      setIsSubmitting(false);
    }
  };

  // Update form field state on input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="get-quote custom-form">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={"Request Quote"} subTitle={"Get Free Consultation"} linear sideHeading />

            <div className="input-box">
              {/* Full Name Input Field */}
              <div className="input-field">
                <label htmlFor="fullName"><FaUser /></label>
                <input
                  type="text"
                  name="fullName"
                  className="box"
                  id="fullName"
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="tel"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Service Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="serviceType"><FaPrint /></label>
                  <select
                    className="select-box"
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Service</option>
                    <option value="Card Design">Card Design</option>
                    <option value="Banner Printing">Banner Printing</option>
                    <option value="Flyer Printing">Flyer Printing</option>
                    <option value="Shirt Printing">Shirt Printing</option>
                    <option value="Package Design">Package Design</option>
                    <option value="Mug Printing">Mug Printing</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>

            </div>

            <div className="input-box">
              {/* Project Title Input Field */}
              <div className="input-field">
                <label htmlFor="projectTitle"><FaEdit /></label>
                <input
                  type="text"
                  name="projectTitle"
                  className="box"
                  id="projectTitle"
                  placeholder="Project Title"
                  value={formData.projectTitle}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Material Type Select Field */}
              <div className="select">
                <div className="select-container">
                  <label htmlFor="materialType"><FaBox /></label> {/* Optional: Replace with an icon like FaBox */}
                  <select
                    className="select-box"
                    id="materialType"
                    name="materialType"
                    value={formData.materialType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Material</option>
                    <option value="Glossy Paper">Glossy Paper</option>
                    <option value="Matte Paper">Matte Paper</option>
                    <option value="Cotton Fabric">Cotton Fabric</option>
                    <option value="Ceramic">Ceramic</option>
                    <option value="Vinyl">Vinyl</option>
                    <option value="Kraft Board">Kraft Board</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>

            </div>

            <div className="input-box">
              {/* Quantity Input Field */}
              <div className="input-field">
                <label htmlFor="quantity"><FaBox /></label>
                <input
                  type="number"
                  name="quantity"
                  className="box"
                  id="quantity"
                  placeholder="Quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                  min="1"
                />
              </div>

              {/* Dimensions Input Field */}
              <div className="input-field">
                <label htmlFor="dimensions"><FaRuler /></label>
                <input
                  type="text"
                  name="dimensions"
                  className="box"
                  id="dimensions"
                  placeholder="Size/Dimensions"
                  value={formData.dimensions}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              <div className="input-field">
                <label htmlFor="designLink"><FaLink /></label> {/* Optional: Replace icon */}
                <input
                  type="url"
                  name="designLink"
                  className="box"
                  id="designLink"
                  placeholder="Paste Design Link"
                  value={formData.designLink}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Address Input Field */}
              <div className="input-field">
                <label htmlFor="address"><FaMapMarkerAlt /></label>
                <input
                  type="text"
                  name="address"
                  className="box"
                  id="address"
                  placeholder="Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

            </div>

            <div className="input-box">
             {/* Date Input Field */}
              <div className="input-field">
                <label htmlFor="date"><FaCalendar /></label>
                <input
                  type="text"
                  name="date"
                  placeholder="Date"
                  id="date"
                  className="box"
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Time Input Field */}
              <div className="input-field">
                <label htmlFor="time"><FaClock /></label>
                <input
                  type="text"
                  name="time"
                  placeholder="Time"
                  id="time"
                  className="box"
                  onFocus={(e) => e.target.type = 'time'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Additional Requirements Text Area */}
            <div className="input-field text-area">
              <label htmlFor="requirements"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="requirements"
                className="box"
                id="requirements"
                placeholder="Additional Requirements"
                value={formData.requirements}
                onChange={handleInputChange}
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <span className="alert" id="msg">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </span>
            )}
            {mailSent && <span className="alert success" id="msg">Quote Request Submitted Successfully</span>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;